import { useEffect, useRef, useState, useCallback } from "react";
import NotificationFormView from "./view";
import {
  globalStyles,
  noop,
  SUPPORTED_COUNTRIES,
} from "@client/shared/constants";
import style from "./style";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Service from "./service";
import {
  DEFAULT_ERROR_MESSAGE,
  PhoneRegexE164,
} from "@client/shared/constants";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PageLoader from "@client/modules/shared/page-loader";

const NOTIFICATION_TRIGGER_TYPES = {
  PHONE_NUMBER: "phone-number",
  EXTENSION: "extension",
  VOICEMAIL_NOTIFICATION: "voicemail-notification",
};
const defaultCountryIso = "US";
const defaultCountryCallingCode = "+1";
let deliveryFlowHelper = null;
const SMS_FROM_CACHE_TTL_MS = 5 * 60 * 1000;
let smsFromOptionsCache = {
  data: null,
  fetchedAt: 0,
  inFlightPromise: null,
};

const defaultState = {
  callDirection: "Outbound",
  delivery: {
    email: "",
    message: "",
    ringCentralTeams: "",
    sms: "",
    sms_from: "",
    webhook: "",
    showAdvance: false,
    json: null,
    isJsonError: false,
    headers: [],
    webhookId: null,
    webhooks: [],
    selectedTimezone: null,
    ringout: {},
  },
  deliverySteps: [],
  externalNumber: defaultCountryCallingCode,
  extensionNumber: "",
  externalNumberError: null,
  extensionNumberError: null,
  isLoading: false,
  name: "",
  switches: {
    allUsers: true,
    users: true,
    email: true,
    extensionPattern: false,
    siteExtensions: false,
    webhook: false,
    ringCentralApp: false,
    sms: false,
    ringout: false,
    sendImmediate: true,
  },
  triggers: {
    extensionPattern: "",
    users: [],
    sites: [],
  },
  triggerNotificationType: NOTIFICATION_TRIGGER_TYPES.PHONE_NUMBER,
  valuesChanged: false,
  isParsingPhoneNumber: false,
  openWebhookDialog: false,
  showCodeInputs: false,
  anchorEl: null,
  countryList: [],
  selectedCountry: SUPPORTED_COUNTRIES.find(
    (country) => country.isoCode === defaultCountryIso,
  ),
  error: {},
  workflowResetModal: {},
};

const NotificationForm = ({
  currentSection = 0,
  onChange = noop,
  notification = null,
  showAll = true,
  onParsingPhoneNumber = noop,
  onPhoneNumParsedSuccess = noop,
  countries = [],
  sites = [],
  teams = [],
  users = [],
  loadedTeams = false,
  loadedCountries = false,
  loadedSites = false,
  loadedUsers = false,
  loadedExtension = false,
  hasValidationError = noop,
  onNewTeamCreated = noop,
}) => {
  const [state, setState] = useState({
    ...defaultState,
    isLoading: !!notification,
    users: [],
    sites: [],
    teams: [],
    timezones: [],
    extensionList: [],
    smsFromOptions: [],
    smsFromOptionsLoading: false,
  });
  const jsonEditorRef = useRef(null);

  const requestSmsFromNumbers = useCallback(async (forceRefresh = false) => {
    const hasFreshCache =
      !forceRefresh &&
      Array.isArray(smsFromOptionsCache.data) &&
      Date.now() - smsFromOptionsCache.fetchedAt < SMS_FROM_CACHE_TTL_MS;

    if (hasFreshCache) {
      return {
        data: smsFromOptionsCache.data,
        error: null,
      };
    }

    if (smsFromOptionsCache.inFlightPromise) {
      return smsFromOptionsCache.inFlightPromise;
    }

    smsFromOptionsCache.inFlightPromise = (async () => {
      try {
        const { data, error } = await Service.getSmsFromNumbers();
        if (error) {
          return { data: [], error };
        }

        const phoneNumbers = data?.phoneNumbers || [];
        smsFromOptionsCache.data = phoneNumbers;
        smsFromOptionsCache.fetchedAt = Date.now();

        return {
          data: phoneNumbers,
          error: null,
        };
      } catch (error) {
        return { data: [], error };
      } finally {
        smsFromOptionsCache.inFlightPromise = null;
      }
    })();

    return smsFromOptionsCache.inFlightPromise;
  }, []);

  const fetchTimezoneList = useCallback(async () => {
    const { data, error } = await Service.getTimezoneList();
    if (error) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } else {
      if (data && data.length > 0) {
        data.sort((countryA, countryB) =>
          countryA.name > countryB.name ? 1 : -1,
        );
        setState((prevState) => ({
          ...prevState,
          timezones: data || [],
          delivery: {
            ...prevState.delivery,
            selectedTimezone: prevState.delivery?.selectedTimezone
              ? prevState.delivery?.selectedTimezone
              : (data || []).find((item) => item.name === "GMT") || null,
          },
        }));
      }
    }
  }, []);

  const fetchExtension = useCallback(async () => {
    const { data, error } = await Service.getExtensions();
    if (error) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } else {
      setState((prevState) => ({
        ...prevState,
        extensionList: (data || []).filter(
          (item) => item?.name && item?.extensionNumber,
        ),
      }));
    }
  }, []);

  // //get all users
  // const fetchAllUser = async () => {
  //   setState((prevState) => ({
  //     ...prevState,
  //     isLoading: true,
  //   }));
  //   const { data, error } = await Service.getUser(getRCAccountId());
  //   if (error) {
  //     toast.error();
  //   } else {
  //     setState((prevState) => ({
  //       ...prevState,
  //       users: data,
  //       isLoading: false,
  //     }));
  //   }
  // };

  // //get all site
  // const fetchAllSite = async () => {
  //   setState((prevState) => ({
  //     ...prevState,
  //     isLoading: true,
  //   }));
  //   const { data, error } = await Service.getAllSite(getRCAccountId());
  //   if (error) {
  //     toast.error();
  //   } else {
  //     setState((prevState) => ({
  //       ...prevState,
  //       sites: data,
  //       isLoading: false,
  //     }));
  //   }
  // };

  // //get all team
  // const fetchAllTeam = async () => {
  //   setState((prevState) => ({
  //     ...prevState,
  //     isLoading: true,
  //   }));
  //   const { data, error } = await Service.getAllTeams(getRCAccountId());
  //   if (error) {
  //     toast.error();
  //   } else {
  //     setState((prevState) => ({
  //       ...prevState,
  //       teams: data,
  //       isLoading: false,
  //     }));
  //   }
  // };

  useEffect(() => {
    fetchTimezoneList();
    fetchExtension();
  }, []);

  const mapState = (notification = null) => {
    if (!notification) {
      return;
    }
    setState((prevState) => {
      const webhooks = (notification.webhook || "")
        .split(",")
        .filter((webhook) => !!webhook)
        .map((webhook, index) => {
          let json = {};
          if (Array.isArray(notification.payload)) {
            json = (notification.payload || []).reduce((acc, item) => {
              if (item.__webhookAddress === webhook) {
                delete item["__webhookAddress"];
                acc = {
                  ...acc,
                  ...item,
                };
              }
              return acc;
            }, {});
          } else {
            json = notification.payload || {};
          }
          let headers = [];
          if (Array.isArray(notification.headers)) {
            headers = notification.headers.filter(
              (item) => item.__webhookAddress === webhook,
            );
          } else {
            headers = Object.entries(notification.headers || {}).reduce(
              (acc, [key, value], ind) => {
                acc.push({
                  id: Date.now() + index + ind,
                  key,
                  value,
                });
                return acc;
              },
              [],
            );
          }

          return {
            id: Date.now() + index,
            webhook,
            json,
            headers,
          };
        });

      const updatedState = {
        ...prevState,
        isLoading: false,
        triggers: { ...prevState.triggers },
        delivery: { ...prevState.delivery },
        switches: { ...prevState.switches },
      };
      if (notification) {
        updatedState.name = notification.name;
        updatedState.callDirection = notification.call_direction;
        updatedState.externalNumber = notification.external_phone_number;
        updatedState.extensionNumber =
          (state.extensionList || []).find((item) => {
            return item.extensionNumber == notification.extension_number;
          }) || notification.extension_number;
        updatedState.delivery.selectedTimezone = notification.timezone || {};
        updatedState.voicemail_extensions =
          (state.extensionList || []).find((item) => {
            return item.extensionNumber == notification.voicemail_extension;
          }) ||
          notification.voicemail_extension ||
          "";
        updatedState.triggerNotificationType = notification.is_extension_number
          ? NOTIFICATION_TRIGGER_TYPES.EXTENSION
          : notification.external_phone_number
            ? NOTIFICATION_TRIGGER_TYPES.PHONE_NUMBER
            : NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION;
        updatedState.triggers.users =
          notification.users === "All Users"
            ? []
            : notification.users
              ? notification.users.split(",").map((id) => {
                  return (
                    (users || []).find(
                      (user) => parseInt(user.id) === parseInt(id),
                    ) || {}
                  );
                })
              : [];
        updatedState.triggers.extensionPattern =
          notification.extension_patterns || "";
        updatedState.triggers.sites = notification.extension_sites
          ? notification.extension_sites.split(",").map((name) => {
              return (sites || []).find((site) => site.name === name) || {};
            })
          : [];
        updatedState.delivery.email = notification.emails || "";
        updatedState.delivery.webhooks = webhooks;
        // updatedState.delivery.webhook = notification.webhook || '';
        updatedState.delivery.ringCentralTeams = notification.glip_teams || "";
        updatedState.delivery.sms = notification.sms_to || "";
        // Keep previous behavior for legacy notifications: if sms_from is not
        // saved on the notification, preserve any already resolved in-form
        // value (e.g. auto-selected account main number from SMS-from options).
        updatedState.delivery.sms_from =
          notification.sms_from || prevState.delivery.sms_from || "";
        updatedState.delivery.ringout = {
          extension: notification.ringout_extension,
          extensionPhoneNumber: { phoneNumber: notification.ringout_to_number },
          phoneNumber: notification.ringout_from_number,
        };
        updatedState.delivery.message = notification.message || "";
        // updatedState.delivery.json = notification.payload || null;
        // updatedState.delivery.headers = notification.headers
        //   ? Object.keys(notification.headers).map((key, index) => ({
        //       id: `${Date.now()}-${index}`,
        //       key,
        //       value: notification.headers[key],
        //     }))
        //   : [];
        // updatedState.delivery.showAdvance =
        //   notification.payload || notification.headers;
        if (notification.users) {
          updatedState.switches.allUsers = notification.users === "All Users";
          updatedState.switches.users = true;
        } else {
          updatedState.switches.allUsers = true;
          updatedState.switches.users = false;
        }

        updatedState.switches.extensionPattern =
          !!notification.extension_patterns;
        updatedState.switches.siteExtensions = !!notification.extension_sites;

        updatedState.switches.email = !!notification.emails;
        updatedState.switches.webhook = !!notification.webhook;
        updatedState.switches.ringCentralApp = !!notification.glip_teams;
        updatedState.switches.sms = !!notification.sms_to;
        updatedState.switches.ringout =
          !!notification.ringout_extension &&
          !!notification.ringout_from_number &&
          !!notification.ringout_to_number;
        updatedState.switches.sendImmediate = !!notification.is_immediate;
        updatedState.deliverySteps = (notification?.delay_config || []).map(
          (item) => {
            if (item.type === "delay" && item.delayUnit === "min") {
              if (!item.valueParsed) {
                item.value = item.value / 60;
              }
              item.valueParsed = true;
            }
            if (item.type === "ringout") {
              item.value = {
                extension:
                  item.value?.extension || item.value?.ringout_extension,
                extensionPhoneNumber: item.value?.extensionPhoneNumber || {
                  phoneNumber: item.value?.ringout_to_number,
                },
                phoneNumber:
                  item.value?.phoneNumber || item.value?.ringout_from_number,
              };
            }
            return item;
          },
        );
      }
      return updatedState;
    });
  };

  const handleOpenWebhook = (isWorkFlow = false, webhook) => {
    setState((prevState) => {
      let updatedState = {};

      if (webhook) {
        updatedState = {
          showCodeInputs:
            webhook?.headers?.length || Object.keys(webhook?.json || {}).length,
          delivery: {
            ...prevState.delivery,
            webhookId: webhook?.id || -1,
            webhook: webhook?.webhook,
            headers: webhook?.headers,
            json: webhook?.json,
          },
        };
      }
      return {
        ...prevState,
        openWebhookDialog: true,
        isWorkFlow,
        ...updatedState,
      };
    });
  };
  const cancelWEbhookEdit = () => {
    setState((prevState) => ({
      ...prevState,
      openWebhookDialog: false,
      showCodeInputs: false,
      delivery: {
        ...prevState.delivery,
        webhook: defaultState.delivery.webhook,
        headers: defaultState.delivery.headers,
        json: defaultState.delivery.json,
        webhookId: defaultState.delivery.webhookId,
      },
    }));
  };

  const addHeader = () => {
    setState((prevState) => ({
      ...prevState,
      valuesChanged: true,
      delivery: {
        ...prevState.delivery,
        headers: [
          ...prevState.delivery.headers,
          { id: Date.now(), key: "", value: "" },
        ],
      },
    }));
  };

  const handleJsonEditer = (jsObject, error) => {
    setState((prevState) => ({
      ...prevState,
      valuesChanged: true,
      delivery: {
        ...prevState.delivery,
        isJsonError: !!error,
        json: jsObject,
      },
    }));
  };

  const handleNotificationChange = (event, error) => {
    const value = event.currentTarget.value;
    const notificationName = event.currentTarget.name;
    let fieldError = error;
    if (!value) {
      fieldError = "Required";
    } else if (value.length > 100) {
      fieldError = "Max:100 char(s).";
    }
    setState((prevState) => ({
      ...prevState,
      error: {
        ...prevState.error,
        [notificationName]: fieldError,
      },
      name: value,
      valuesChanged: true,
    }));
    hasValidationError(fieldError);
  };

  const handleTrigerNotification = (event) => {
    setState((prevState) => ({
      ...prevState,
      triggerNotificationType: event.currentTarget
        ? event.currentTarget.value
        : event.target?.value,
      valuesChanged: true,
      externalNumber: defaultState.externalNumber,
      extensionNumber: defaultState.extensionNumber,
      voicemail_extensions: defaultState.voicemail_extensions,
    }));
    hasValidationError(true);
    // if (
    //   state.deliverySteps?.length &&
    //   state.triggerNotificationType ===
    //     NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION &&
    //   event.currentTarget.value !==
    //     NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
    // ) {
    //   setState((prevState) => ({
    //     ...prevState,
    //     workflowResetModal: {
    //       open: true,
    //       triggerType: event.currentTarget.value,
    //     },
    //   }));
    // } else {
    //   setState((prevState) => ({
    //     ...prevState,
    //     triggerNotificationType: event.currentTarget ? event.currentTarget.value : event.target?.value,
    //     valuesChanged: true,
    //     externalNumber: defaultState.externalNumber,
    //     extensionNumber: defaultState.extensionNumber,
    //     voicemail_extensions: defaultState.voicemail_extensions,
    //   }));
    //   hasValidationError(true);
    // }
  };

  const handlePhoneNumber = (evt, error) => {
    const value = evt.currentTarget.value;
    const name = evt.currentTarget.name;
    let fieldError = error;
    if (!value) {
      fieldError = "Required";
    } else if (value.length < 4) {
      fieldError = "Must be a valid number";
    }
    state.countryList.filter((country) => {
      if (`+${country.callingCode}` == value) {
        setState((prevState) => ({
          ...prevState,
          selectedCountry:
            value == "+1" ? defaultState.selectedCountry : country,
        }));
      }
    });
    setState((prevState) => ({
      ...prevState,
      error: {
        [name]: fieldError,
      },
      externalNumber: value,
      externalNumberError: error,
      valuesChanged: true,
    }));
    hasValidationError(fieldError);
  };

  const handleCountrySelection = (selectedOption) => {
    setState((prevState) => ({
      ...prevState,
      selectedCountry: selectedOption,
      externalNumber: `+${selectedOption.callingCode}`,
    }));
  };

  const handleExtNumber = (extensionNumber) => {
    setState((prevState) => ({
      ...prevState,
      extensionNumber,
      valuesChanged: true,
    }));
    hasValidationError(false);
  };

  const handleNotificationDirection = (event) => {
    setState((prevState) => ({
      ...prevState,
      callDirection: event.currentTarget?.value,
      valuesChanged: true,
    }));
  };

  const handleAffectNotification = (event) => {
    setState((prevState) => ({
      ...prevState,
      switches: {
        ...prevState.switches,
        users: event.currentTarget.checked,
      },
      valuesChanged: true,
    }));
  };

  const handleExtPattern = (event) => {
    setState((prevState) => ({
      ...prevState,
      switches: {
        ...prevState.switches,
        extensionPattern: event.currentTarget.checked,
      },
      error: {
        ...prevState.error,
        extension_pattern: "",
      },

      valuesChanged: true,
    }));
    hasValidationError(!!event.currentTarget.checked);
  };

  const handleSiteExt = (event) => {
    setState((prevState) => ({
      ...prevState,
      switches: {
        ...prevState.switches,
        siteExtensions: event.currentTarget.checked,
      },
      valuesChanged: true,
    }));
  };

  const handleAllUsers = (event) => {
    setState((prevState) => ({
      ...prevState,
      switches: {
        ...prevState.switches,
        allUsers: event.currentTarget.checked,
      },
      triggers: {
        ...prevState.triggers,
        users: [],
      },
      valuesChanged: true,
    }));
  };

  const handleUser = (event, values) => {
    setState((prevState) => ({
      ...prevState,
      triggers: {
        ...prevState.triggers,
        users: values.slice(),
      },
      switches: {
        ...prevState.switches,
        allUsers: false,
      },
      valuesChanged: true,
    }));
  };

  const handleExtension = (event, error) => {
    const extensionPattern = event.currentTarget.value.trim();
    const name = event.currentTarget.name;
    let fieldError = error;
    if (!extensionPattern) {
      fieldError = "Required";
    } else if (extensionPattern.length > 300) {
      fieldError = "Max:300 char(s).";
    }
    setState((prevState) => ({
      ...prevState,
      error: {
        ...prevState.error,
        [name]: fieldError,
      },
      triggers: {
        ...prevState.triggers,
        extensionPattern,
      },
      valuesChanged: true,
    }));
    hasValidationError(fieldError);
  };
  const handleEmailChange = (event, error) => {
    const email = event.currentTarget.value?.trim();
    const name = event.currentTarget.name;
    let fieldError = error;
    if (!email) {
      fieldError = "Required";
    } else if (email.length > 500) {
      fieldError = "Max:500 char(s).";
    }
    setState((prevState) => ({
      ...prevState,
      error: {
        [name]: fieldError,
      },
      delivery: {
        ...prevState.delivery,
        email,
      },
      valuesChanged: true,
    }));
    hasValidationError(fieldError);
  };

  const handleWebhook = (event, error) => {
    const webhook = event.currentTarget.value;
    const name = event.currentTarget.name;
    let fieldError = error;
    if (!webhook) {
      fieldError = "Required";
    } else if (webhook.length > 500) {
      fieldError = "Max:500 char(s).";
    }
    setState((prevState) => ({
      ...prevState,
      error: {
        [name]: fieldError,
      },
      delivery: {
        ...prevState.delivery,
        webhook,
      },
      valuesChanged: true,
    }));
    hasValidationError(fieldError);
  };

  const handleSms = (event, error) => {
    const sms = event.currentTarget.value;
    const name = event.currentTarget.name;
    let fieldError = error;
    if (!sms) {
      fieldError = "Required";
    } else if (sms.length > 500) {
      fieldError = "Max:500 char(s).";
    }
    setState((prevState) => ({
      ...prevState,
      error: {
        [name]: fieldError,
      },
      delivery: {
        ...prevState.delivery,
        sms,
      },
      valuesChanged: true,
    }));
    hasValidationError(fieldError);
  };

  const handleSmsFrom = (event) => {
    const sms_from = event.target.value;
    setState((prevState) => ({
      ...prevState,
      delivery: {
        ...prevState.delivery,
        sms_from,
      },
      valuesChanged: true,
    }));
  };

  const handleRingoutChange = (name, value, isEdit = false) => {
    setState((prevState) => ({
      ...prevState,
      delivery: {
        ...prevState.delivery,
        ringout: {
          ...prevState.delivery.ringout,
          [name]: value,
        },
      },
      valuesChanged: !isEdit,
    }));
  };

  const emailSwitch = (event) => {
    setState((prevState) => ({
      ...prevState,
      switches: {
        ...prevState.switches,
        email: event.currentTarget?.checked,
      },
      valuesChanged: true,
    }));
  };
  const sendImmediateSwitch = (event) => {
    setState((prevState) => {
      let isOpen =
        prevState.deliverySteps && prevState.deliverySteps.length > 0;
      if (!isOpen) {
        isOpen =
          !!prevState.delivery.email ||
          !!prevState.delivery.ringCentralTeams ||
          !!prevState.delivery.sms ||
          !!prevState.delivery.webhook ||
          Object.keys(prevState.delivery.ringout).length > 0;
      }

      if (!isOpen) {
        return {
          ...prevState,
          switches: {
            ...defaultState.switches,
            extensionPattern: prevState.switches.extensionPattern,
            siteExtensions: prevState.switches.siteExtensions,
            allUsers: prevState.switches.allUsers,
            users: prevState.switches.users,
            sendImmediate: event.currentTarget?.checked,
            email: event.currentTarget?.checked,
          },
        };
      }

      return {
        ...prevState,
        workflowResetModal: {
          open: isOpen,
          isChecked: event.currentTarget?.checked,
        },
      };
    });
  };
  const webhookSwitch = (event) => {
    setState((prevState) => ({
      ...prevState,
      switches: {
        ...prevState.switches,
        webhook: event.currentTarget?.checked,
      },
      valuesChanged: true,
    }));
  };
  const glipSwitch = (event) => {
    setState((prevState) => ({
      ...prevState,
      switches: {
        ...prevState.switches,
        ringCentralApp: event.currentTarget?.checked,
      },
      valuesChanged: true,
    }));
  };
  const fetchSmsFromOptions = useCallback(
    async ({
      forceRefresh = false,
      showLoading = true,
      silent = false,
      disableSmsOnError = true,
    } = {}) => {
      if (showLoading) {
        setState((prevState) => ({
          ...prevState,
          smsFromOptionsLoading: true,
        }));
      }
      let smsFromOptions = [];
      try {
        const { data, error } = await requestSmsFromNumbers(forceRefresh);
        smsFromOptions = data || [];
        if (error) {
          if (!silent) {
            toast.error(DEFAULT_ERROR_MESSAGE);
          }
          setState((prevState) => ({
            ...prevState,
            smsFromOptionsLoading: false,
            ...(disableSmsOnError
              ? {
                  switches: { ...prevState.switches, sms: false },
                  smsFromOptions: [],
                  delivery: { ...prevState.delivery, sms_from: "" },
                }
              : {}),
          }));
          return;
        }
      } catch (e) {
        if (!silent) {
          toast.error(DEFAULT_ERROR_MESSAGE);
        }
        setState((prevState) => ({
          ...prevState,
          smsFromOptionsLoading: false,
          ...(disableSmsOnError
            ? {
                switches: { ...prevState.switches, sms: false },
                smsFromOptions: [],
                delivery: { ...prevState.delivery, sms_from: "" },
              }
            : {}),
        }));
        return;
      }

      // Default to the account's main number when it's present in the
      // available options, otherwise fall back to the single option (if
      // only one exists) or leave whatever was previously selected.
      const mainNumberOption = smsFromOptions.find(
        (option) => option?.isMainNumber,
      );
      setState((prevState) => {
        if (!prevState.switches.sms) {
          return { ...prevState, smsFromOptionsLoading: false };
        }
        // Preserve a previously saved sms_from if it's still a valid option
        // (e.g. when editing an existing notification).
        const existingSmsFromIsValid = smsFromOptions.some(
          (option) => option?.phoneNumber === prevState.delivery.sms_from,
        );
        return {
          ...prevState,
          smsFromOptionsLoading: false,
          smsFromOptions,
          delivery: {
            ...prevState.delivery,
            sms_from: existingSmsFromIsValid
              ? prevState.delivery.sms_from
              : mainNumberOption?.phoneNumber ||
                (smsFromOptions.length === 1
                  ? smsFromOptions[0]?.phoneNumber || ""
                  : prevState.delivery.sms_from),
          },
        };
      });
    },
    [requestSmsFromNumbers],
  );

  const smsSwitch = async (event) => {
    let checked = event.currentTarget.checked;
    setState((prevState) => ({
      ...prevState,
      switches: {
        ...prevState.switches,
        sms: event.currentTarget?.checked,
      },
      valuesChanged: true,
    }));
    if (checked === true) {
      await fetchSmsFromOptions();
    } else {
      setState((prevState) => ({
        ...prevState,
        smsFromOptions: [],
        delivery: {
          ...prevState.delivery,
          sms_from: "",
        },
      }));
    }
  };

  const ringoutSwitch = (event) => {
    setState((prevState) => ({
      ...prevState,
      delivery: {
        ...prevState.delivery,
        ringout: {},
      },
      switches: {
        ...prevState.switches,
        ringout: event.currentTarget?.checked,
      },
      valuesChanged: true,
    }));
  };

  const handleChange = (event, values) => {
    setState((prevState) => ({
      ...prevState,
      triggers: {
        ...prevState.triggers,
        sites: values,
      },
      valuesChanged: true,
    }));
  };

  const handleWebhooksChange = (event, error) => {
    const webhook = event.currentTarget.value;
    const name = event.currentTarget.name;
    let fieldError = error;
    if (!webhook) {
      fieldError = "Required";
    } else if (webhook.length > 500) {
      fieldError = "Max:500 char(s).";
    }
    setState((prevState) => ({
      ...prevState,
      error: {
        ...prevState.error,
        [name]: fieldError,
      },
      delivery: {
        ...prevState.delivery,
        webhook,
      },
      valuesChanged: true,
    }));
    hasValidationError(fieldError);
  };

  const handleChecked = (event) => {
    const { checked } = event.target || event.currentTarget;
    if (checked) {
      setState((prevState) => ({
        ...prevState,
        valuesChanged: true,
        showCodeInputs: checked,
        delivery: {
          ...prevState.delivery,
          json: null,
          headers: [],
        },
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        showCodeInputs: checked,
        delivery: {
          ...prevState.delivery,
          json: null,
          headers: [],
        },
      }));
    }
  };

  const handleSnippet = (message) => {
    setState((prevState) => ({
      ...prevState,
      delivery: {
        ...prevState.delivery,
        message,
      },
      valuesChanged: true,
    }));
  };

  const handleTeamChange = (ringCentralTeams) => {
    setState((prevState) => ({
      ...prevState,
      delivery: {
        ...prevState.delivery,
        ringCentralTeams,
      },
      valuesChanged: true,
    }));
  };

  const handlePrefferedTimezone = (selectedOption) => {
    setState((prevState) => ({
      ...prevState,
      delivery: {
        ...prevState.delivery,
        selectedTimezone: selectedOption,
      },
      valuesChanged: true,
    }));
  };

  const handleVoicemailExtension = (e, value) => {
    setState((prevState) => ({
      ...prevState,
      voicemail_extensions: value,
      valuesChanged: true,
    }));
  };

  //   // Data fetchers
  const fetchCountryList = async () => {
    const { data, error } = await Service.getCountryList();
    if (error) {
      toast.error("Something went wrong, please contact system administrator.");
    } else {
      if (data && data.length > 0) {
        data.sort((countryA, countryB) =>
          countryA.name > countryB.name ? 1 : -1,
        );

        setState((prevState) => ({
          ...prevState,
          countryList: data,
        }));
      }
    }
  };

  useEffect(() => {
    fetchCountryList();
  }, []);

  //   const fetchSiteList = async () => {
  //     const { data, error } = await RingCentralService.getAllSites(
  //       getRCAccountId()
  //     );
  //     if (error) {
  //       showToast(DEFAULT_ERROR_MESSAGE, TOAST_TYPES.ERROR);
  //     } else {
  //       setState(prevState => ({
  //         ...prevState,
  //         siteList: data,
  //       }));
  //     }
  //   };

  //   const fetchUserList = async () => {
  //     const { data, error } = await RingCentralService.getAllUsers(
  //       getRCAccountId()
  //     );
  //     if (error) {
  //       showToast(DEFAULT_ERROR_MESSAGE, TOAST_TYPES.ERROR);
  //     } else {
  //       setState(prevState => ({
  //         ...prevState,
  //         userList: data,
  //       }));
  //     }
  //   };

  const onCancelWebhooks = () => {
    setState((prevState) => ({
      ...prevState,
      openWebhookDialog: false,
      showCodeInputs: false,
      delivery: {
        ...prevState.delivery,
        webhook: defaultState.delivery.webhook,
        headers: defaultState.delivery.headers,
        json: defaultState.delivery.json,
        webhookId: defaultState.delivery.webhookId,
      },
    }));
  };

  const parsePhoneNumber = async () => {
    const payload = {
      originalStrings: [state.externalNumber],
    };
    const { data, error } = await Service.parsePhoneNumber(payload);
    if (error) {
      toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
    }
    const phoneNumbers = data && data.phoneNumbers.map((item) => item.e164);
    setState((prevState) => ({
      ...prevState,
      externalNumber: phoneNumbers && phoneNumbers[0],
      isParsingPhoneNumber: false,
    }));
    onPhoneNumParsedSuccess(true);
  };

  const handleParsePhoneNumber = () => {
    setState((prevState) => ({
      ...prevState,
      isParsingPhoneNumber: true,
    }));
  };
  useEffect(() => {
    // (async () => {
    //   await fetchCountryList();
    //   await fetchUserList();
    //   await fetchSiteList();
    // })();
    return () => {
      setState(defaultState);
    };
  }, []);

  useEffect(() => {}, [state.delivery.json]);

  useEffect(() => {
    // if (loadedCountries && loadedSites && loadedUsers && loadedTeams) {

    mapState(notification);
    // }
  }, [notification, users, state.extensionList]);

  useEffect(() => {
    // When editing an existing notification with SMS enabled, the SMS-from
    // dropdown options are never fetched unless the user toggles SMS off/on.
    // Fetch them here so the saved sms_from value has selectable options.
    if (notification?.sms_to && state.switches.sms) {
      fetchSmsFromOptions();
    }
  }, [notification, state.switches.sms]);

  useEffect(() => {
    // Prefetch SMS-from options when the form loads so the SMS toggle
    // feels instant if the user enables it later.
    fetchSmsFromOptions({
      showLoading: false,
      silent: true,
      disableSmsOnError: false,
    });
  }, []);

  useEffect(() => {
    if (state.isLoading) {
      return;
    }

    onChange(
      {
        callDirection: state.callDirection,
        delivery: { ...state.delivery },
        externalNumber: state.externalNumber,
        externalNumberError: state.externalNumberError,
        extensionNumber: state.extensionNumber,
        extensionNumberError: state.extensionNumberError,
        voicemail_extensions: state.voicemail_extensions,
        isExtensionNumber:
          state.triggerNotificationType ===
          NOTIFICATION_TRIGGER_TYPES.EXTENSION,
        name: state.name,
        switches: { ...state.switches },
        triggers: { ...state.triggers },
        deliverySteps: state.deliverySteps,
        isVoicemailExtensions:
          state.triggerNotificationType === "voicemail-notification",
      },
      state.valuesChanged,
    );
  }, [
    state.callDirection,
    state.delivery,
    state.externalNumber,
    state.name,
    state.switches,
    state.triggers,
    state.isParsingPhoneNumber,
    state.extensionNumber,
    state.triggerNotificationType,
    state.voicemail_extensions,
    state.deliverySteps,
  ]);

  useEffect(() => {
    if (state.externalNumber.length < 3) {
      return;
    }
    // if (state.isParsingPhoneNumber) {
    //   showToast('Validating phone #, please wait..', TOAST_TYPES.INFO);
    // }
    onParsingPhoneNumber(state.isParsingPhoneNumber, state.valuesChanged);
  }, [state.isParsingPhoneNumber]);

  // if (state.isLoading) {
  //   return <PageLoader className="ml-4" />;
  // }

  const handleUsers = (event, values) => {
    setState((prevState) => ({
      ...prevState,
      triggers: {
        ...prevState.triggers,
        users: values.slice(),
      },
      switches: {
        ...prevState.switches,
        allUsers: false,
      },
      valuesChanged: true,
    }));
  };
  const handleKeyDown = (evt) => {
    if (+evt.keyCode === 8 && state.externalNumber.length < 2) {
      evt.preventDefault();
    }
  };

  const renderWebhookHeaders = (headers = []) => (
    <Box sx={style.webhookHeadersWrapper}>
      {headers.map(({ id, key, value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "8px",
          }}
        >
          <TextField
            size="small"
            label="Header Name"
            value={key}
            onChange={(event) => {
              const inputValue = event.currentTarget.value;
              setState((prevState) => {
                const updatedHeaders = prevState.delivery.headers.map(
                  (header) => ({
                    ...header,
                    key: header.id === id ? inputValue : header.key,
                  }),
                );
                return {
                  ...prevState,
                  valuesChanged: true,
                  delivery: {
                    ...prevState.delivery,
                    headers: updatedHeaders,
                  },
                };
              });
            }}
          />
          <TextField
            size="small"
            label="Header Value"
            value={value}
            onChange={(event) => {
              const inputValue = event.currentTarget.value;
              setState((prevState) => {
                const updatedHeaders = prevState.delivery.headers.map(
                  (header) => ({
                    ...header,
                    value: header.id === id ? inputValue : header.value,
                  }),
                );
                return {
                  ...prevState,
                  valuesChanged: true,
                  delivery: {
                    ...prevState.delivery,
                    headers: updatedHeaders,
                  },
                };
              });
            }}
          />
          <Tooltip title="Remove" placement="top-start">
            <img
              alt="Remove Header"
              src="/assets/close-red.svg"
              style={{ marginLeft: "16px", cursor: "pointer" }}
              onClick={() => {
                setState((prevState) => {
                  const updatedHeaders = prevState.delivery.headers.filter(
                    (header) => header.id !== id,
                  );
                  return {
                    ...prevState,
                    valuesChanged: true,
                    delivery: {
                      ...prevState.delivery,
                      headers: updatedHeaders,
                    },
                  };
                });
              }}
            />
          </Tooltip>
        </Box>
      ))}
    </Box>
  );

  const handleInsertSnippet = (option) => {
    let sel, range;
    sel = window.getSelection();

    if (
      jsonEditorRef.current &&
      !jsonEditorRef.current.contains(sel?.baseNode?.parentNode)
    ) {
      return;
    }
    // if (!(sel?.baseNode?.parentNode?.id || '').includes('json-text-editor')) {
    //   return;
    // }

    if (sel.getRangeAt && sel.rangeCount) {
      range = sel.getRangeAt(0);
      range.deleteContents();
      var el = document.createElement("div");
      el.innerHTML = `"${option.value}"`;
      var frag = document.createDocumentFragment(),
        node;
      while ((node = el.firstChild)) {
        frag.appendChild(node);
      }
      var firstNode = frag.firstChild;
      range.insertNode(frag);
      range.setStartAfter(firstNode);
      range.setEndAfter(firstNode);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const handleWebhookEdit = ({
    webhookId = null,
    webhook = "",
    headers = [],
    json = {},
  }) => {
    setState((prevState) => {
      let webhooks = [...prevState.delivery.webhooks];
      if (webhookId) {
        webhooks = webhooks.map((item) => {
          if (webhookId === item.id) {
            return {
              ...item,
              webhook: (webhook || "").trim(),
              headers,
              json,
            };
          }
          return item;
        });
      } else {
        webhooks.push({
          id: Date.now(),
          webhook: (webhook || "").trim(),
          headers,
          json,
        });
      }
      if (!prevState.switches.sendImmediate) {
        deliveryFlowHelper &&
          deliveryFlowHelper.addWebhook({
            webhook: (webhook || "").trim(),
            headers,
            json,
          });
      }
      return {
        ...prevState,
        openWebhookDialog: false,
        showCodeInputs: false,
        delivery: {
          ...prevState.delivery,
          webhook: defaultState.delivery.webhook,
          headers: defaultState.delivery.headers,
          json: defaultState.delivery.json,
          webhookId: defaultState.delivery.webhookId,
          webhooks: prevState.switches.sendImmediate
            ? webhooks
            : prevState.delivery.webhooks,
        },
      };
    });
  };

  const onDeliveryStepsUpdate = (deliverySteps) => {
    setState((prevState) => ({
      ...prevState,
      deliverySteps,
      valuesChanged: true,
    }));
  };

  const handleSelectionOption = (action = "edit", item) => {
    if (action === "edit") {
      setState((prevState) => ({
        ...prevState,
        openWebhookDialog: true,
        showCodeInputs:
          item.headers.length || Object.keys(item.json || {}).length,
        delivery: {
          ...prevState.delivery,
          webhookId: item.id,
          webhook: item.webhook,
          headers: item.headers,
          json: item.json,
        },
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        delivery: {
          ...prevState.delivery,
          webhooks: prevState.delivery.webhooks.filter(
            (webhook) => webhook.id !== item.id,
          ),
        },
      }));
    }
  };

  if (state.isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <NotificationFormView
        showAll={showAll}
        currentSection={currentSection}
        name={state.name}
        triggerNotificationType={state.triggerNotificationType}
        isParsingPhoneNumber={state.isParsingPhoneNumber}
        externalNumber={state.externalNumber}
        countries={countries}
        onPhoneNumParsedSuccess={onPhoneNumParsedSuccess}
        externalNumberError={state.externalNumberError}
        parsePhoneNumber={parsePhoneNumber}
        extensionNumber={state.extensionNumber}
        callDirection={state.callDirection}
        switches={state.switches}
        triggers={state.triggers}
        sites={sites}
        users={users}
        teams={teams}
        delivery={state.delivery}
        handleInsertSnippet={handleInsertSnippet}
        openWebhookDialog={state.openWebhookDialog}
        showCodeInputs={state.showCodeInputs}
        renderWebhookHeaders={renderWebhookHeaders}
        handleWebhookEdit={handleWebhookEdit}
        handleNotificationChange={handleNotificationChange}
        handleTrigerNotification={handleTrigerNotification}
        handlePhoneNumber={handlePhoneNumber}
        handleExtNumber={handleExtNumber}
        handleNotificationDirection={handleNotificationDirection}
        handleAffectNotification={handleAffectNotification}
        handleExtPattern={handleExtPattern}
        handleSiteExt={handleSiteExt}
        handleAllUsers={handleAllUsers}
        handleUser={handleUser}
        handleExtension={handleExtension}
        handleChange={handleChange}
        handleChecked={handleChecked}
        handleSnippet={handleSnippet}
        handleEmailChange={handleEmailChange}
        handleWebhook={handleWebhook}
        handleSms={handleSms}
        handleSmsFrom={handleSmsFrom}
        handleRingoutChange={handleRingoutChange}
        emailSwitch={emailSwitch}
        sendImmediateSwitch={sendImmediateSwitch}
        webhookSwitch={webhookSwitch}
        glipSwitch={glipSwitch}
        smsSwitch={smsSwitch}
        ringoutSwitch={ringoutSwitch}
        handleTeamChange={handleTeamChange}
        handleOpenWebhook={handleOpenWebhook}
        cancelWEbhookEdit={cancelWEbhookEdit}
        addHeader={addHeader}
        selectedCountry={state.selectedCountry}
        countryList={state.countryList}
        handleCountrySelection={handleCountrySelection}
        handleParsePhoneNumber={handleParsePhoneNumber}
        handleUsers={handleUsers}
        onCancelWebhooks={onCancelWebhooks}
        handleWebhooksChange={handleWebhooksChange}
        handleKeyDown={handleKeyDown}
        timezones={state.timezones}
        handlePrefferedTimezone={handlePrefferedTimezone}
        voicemail_extensions={state.voicemail_extensions}
        extensionList={state.extensionList}
        handleVoicemailExtension={handleVoicemailExtension}
        error={state.error}
        hasValidationError={hasValidationError}
        deliverySteps={state.deliverySteps}
        onDeliveryStepsUpdate={onDeliveryStepsUpdate}
        notification={notification}
        onDeliveryFlowReady={(helper) => {
          deliveryFlowHelper = helper;
        }}
        handleSelectionOption={handleSelectionOption}
        handleJsonEditer={handleJsonEditer}
        jsonEditorRef={jsonEditorRef}
        loadedExtension={loadedExtension}
        onNewTeamCreated={onNewTeamCreated}
        smsFromOptions={state.smsFromOptions}
        smsFromOptionsLoading={state.smsFromOptionsLoading}
      />
      <Dialog open={state.workflowResetModal?.open}>
        <DialogContent
          sx={{ minHeight: "200px", minWidth: "100px", maxWidth: "400px" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Are you sure?</Typography>
            <IconButton>
              <CloseIcon
                sx={{ color: "grey.600" }}
                fontSize="small"
                onClick={() => {
                  setState((prevState) => ({
                    ...prevState,
                    workflowResetModal: {},
                  }));
                }}
              />
            </IconButton>
          </Box>
          <Typography sx={{ mt: 4 }} variant="subtitle2">
            This will reset all the changes you have made related to delivery of
            this notification.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setState((prevState) => ({
                ...prevState,
                workflowResetModal: {},
              }));
            }}
            sx={{ color: "grey.600", mr: 4 }}
            variant="text"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setState((prevState) => ({
                ...prevState,
                switches: {
                  ...defaultState.switches,

                  extensionPattern: prevState.switches.extensionPattern,
                  siteExtensions: prevState.switches.siteExtensions,
                  allUsers: prevState.switches.allUsers,
                  users: prevState.switches.users,
                  sendImmediate: prevState.workflowResetModal?.isChecked,
                  email: prevState.workflowResetModal?.isChecked,
                },
                deliverySteps: [],
                // delivery: defaultState.delivery,
                smsFromOptions: [],
                delivery: {
                  ...prevState.delivery,
                  email: "",
                  ringCentralTeams: "",
                  sms: "",
                  sms_from: "",
                  webhook: "",
                  json: null,
                  isJsonError: false,
                  headers: [],
                  webhookId: null,
                  webhooks: [],
                  ringout: {},
                },
                valuesChanged: true,
                workflowResetModal: {},
              }));
            }}
            variant="contained"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationForm;
